'use client'

import {
    type ComponentType,
    type HTMLAttributes,
    type ReactElement,
    type ReactNode,
    type RefAttributes,
    useEffect,
    useMemo,
    useState
} from 'react'
import type {CalendarDateTimeFormatter} from '@fitro/date-time/display'
import {cn} from './cn'
import {createSegmentedTabs} from './SegmentedTabs'

export type CalendarPeriodView = 'day' | 'week' | 'month'

/** Popover on desktop, bottom sheet on compact screens; each feature package supplies its own. */
export interface CalendarPeriodOverlayProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    title: string
    closeText: string
    placement: 'bottom'
    matchTriggerWidth: false
    className: string
    trigger: ReactElement<HTMLAttributes<HTMLElement> & RefAttributes<HTMLElement>>
    children: ReactNode
}

export interface CalendarPeriodStep {
    onClick: () => void
    disabled?: boolean
}

export interface CalendarPeriodPickerLabels {
    selectPeriod: string
    close: string
    viewSwitcher: string
    /** Labels for the views the surface offers. */
    views: Partial<Record<CalendarPeriodView, string>>
    previousPeriod: string
    nextPeriod: string
    previousMonth: string
    nextMonth: string
    today?: string
}

export interface CalendarPeriodPickerProps {
    view: CalendarPeriodView
    views: readonly CalendarPeriodView[]
    onViewChange: (view: CalendarPeriodView) => void
    /** Hides the view switcher from the `lg` breakpoint, where the surface always shows a week. */
    viewSwitcherCompactOnly?: boolean
    /** Selected day, the start of the selected week, or the first day of the selected month. */
    anchor: Date
    onAnchorChange: (date: Date) => void
    title: string
    accessibleTitle?: string
    hideViewLabelOnCompact?: boolean
    previous: CalendarPeriodStep
    next: CalendarPeriodStep
    /** Renders the "today" button next to the period frame. */
    onToday?: () => void
    /** Studio day as a date-picker carrier; null while the studio context is unavailable. */
    today: Date | null
    /** Bounds the day picker by whole weeks; the public schedule loads complete weeks only. */
    minWeekStart?: Date
    maxWeekStart?: Date
    dateTime: CalendarDateTimeFormatter
    labels: CalendarPeriodPickerLabels
    appearance?: 'app' | 'landing'
    className?: string
    frameClassName?: string
    'data-testid'?: string
}

const startOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())
const addDays = (date: Date, days: number) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)
const startOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1)
const addMonths = (date: Date, months: number) => new Date(date.getFullYear(), date.getMonth() + months, 1)
const startOfIsoWeek = (date: Date) => addDays(date, -((date.getDay() + 6) % 7))
const isSameDay = (left: Date, right: Date) => startOfDay(left).getTime() === startOfDay(right).getTime()
const pickerMonthFor = (anchorTime: number, minMonthTime: number | undefined) => {
    const anchorMonth = startOfMonth(new Date(anchorTime))
    return minMonthTime !== undefined && anchorMonth.getTime() < minMonthTime ? new Date(minMonthTime) : anchorMonth
}
const toDateKey = (date: Date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')
].join('-')

const APPEARANCE = {
    app: {
        frame: 'h-11 border-gray-200 dark:border-gray-700 dark:bg-gray-900',
        divider: 'border-gray-200 dark:border-gray-700',
        step: 'text-gray-700 hover:bg-gray-50 focus-visible:ring-primary-500/40 dark:text-gray-200 dark:hover:bg-gray-800',
        trigger: 'hover:bg-gray-50 focus-visible:ring-primary-500/40 dark:hover:bg-gray-800',
        viewLabel: 'text-gray-600 dark:text-gray-300',
        separator: 'text-gray-400',
        title: 'text-gray-900 dark:text-white',
        today: 'h-11 border-gray-200 font-semibold text-gray-700 hover:bg-gray-50 focus-visible:ring-primary-500/40 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
    },
    landing: {
        frame: 'h-12 border-[var(--landing-neutral-100)]',
        divider: 'border-[var(--landing-neutral-100)]',
        step: 'text-[var(--landing-neutral-700)] hover:bg-[var(--landing-neutral-50)] hover:text-[var(--landing-primary-600)] focus-visible:ring-[var(--landing-focus-ring)]',
        trigger: 'hover:bg-[var(--landing-neutral-50)] focus-visible:ring-[var(--landing-focus-ring)]',
        viewLabel: 'text-[var(--landing-neutral-500)]',
        separator: 'text-[var(--landing-neutral-300)]',
        title: 'text-[var(--landing-neutral-900)]',
        today: 'h-12 border-[var(--landing-neutral-100)] font-medium text-[var(--landing-neutral-700)] hover:bg-[var(--landing-neutral-50)] focus-visible:ring-[var(--landing-focus-ring)]'
    }
} as const

const ChevronIcon = ({direction, className}: {direction: 'left' | 'right' | 'down'; className: string}) => (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={direction === 'left' ? 'M15 19l-7-7 7-7' : direction === 'right' ? 'M9 5l7 7-7 7' : 'M19 9l-7 7-7-7'}
        />
    </svg>
)

const PANEL_NAV_BUTTON = 'flex h-9 w-9 items-center justify-center rounded-lg text-gray-600 hover:bg-gray-100 disabled:cursor-not-allowed disabled:text-gray-300 disabled:hover:bg-transparent dark:text-gray-300 dark:hover:bg-gray-800 dark:disabled:text-gray-600'

// View segments are buttons, so the SegmentedTabs link renderer is never used.
const ViewTabs = createSegmentedTabs(({scroll: _scroll, ...props}) => <a {...props}/>)

/**
 * Period navigation for calendars: previous/next, a picker for the day, week or month, and the view switch.
 * Build it per app with `createCalendarPeriodPicker({Overlay})`.
 */
export function createCalendarPeriodPicker({Overlay}: {Overlay: ComponentType<CalendarPeriodOverlayProps>}) {
    return function CalendarPeriodPicker({
        view,
        views,
        onViewChange,
        viewSwitcherCompactOnly = false,
        anchor,
        onAnchorChange,
        title,
        accessibleTitle = title,
        hideViewLabelOnCompact = false,
        previous,
        next,
        onToday,
        today,
        minWeekStart,
        maxWeekStart,
        dateTime,
        labels,
        appearance = 'app',
        className,
        frameClassName,
        'data-testid': testId
    }: CalendarPeriodPickerProps) {
        const styles = APPEARANCE[appearance]
        const viewLabel = labels.views[view] ?? ''
        // The first allowed week is fully visible in the grid of the month it ends in.
        const minMonth = minWeekStart ? startOfMonth(addDays(minWeekStart, 6)) : null
        const maxMonth = maxWeekStart ? startOfMonth(maxWeekStart) : null
        const anchorTime = anchor.getTime()
        const minMonthTime = minMonth?.getTime()
        const [open, setOpen] = useState(false)
        const [pickerMonth, setPickerMonth] = useState(() => pickerMonthFor(anchorTime, minMonthTime))
        const pickerDays = useMemo(() => {
            const gridStart = startOfIsoWeek(pickerMonth)
            return Array.from({length: 42}, (_, index) => addDays(gridStart, index))
        }, [pickerMonth])
        const pickerWeekdays = useMemo(
            () => Array.from({length: 7}, (_, index) => dateTime.formatWeekday(index + 1, {context: 'sentence'})),
            [dateTime]
        )

        useEffect(() => {
            if (open) setPickerMonth(pickerMonthFor(anchorTime, minMonthTime))
        }, [anchorTime, minMonthTime, open])

        const isWeekDisabled = (weekStart: Date) =>
            Boolean((minWeekStart && weekStart < startOfDay(minWeekStart)) || (maxWeekStart && weekStart > startOfDay(maxWeekStart)))

        const selectDate = (date: Date) => {
            onAnchorChange(date)
            setOpen(false)
        }

        const renderStep = (step: CalendarPeriodStep, direction: 'left' | 'right', label: string) => (
            <button
                type="button"
                aria-label={label}
                disabled={step.disabled}
                onClick={step.onClick}
                className={cn(
                    'flex h-full w-11 shrink-0 items-center justify-center transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset',
                    styles.step,
                    step.disabled && 'cursor-not-allowed opacity-45'
                )}
            >
                <ChevronIcon direction={direction} className="h-5 w-5"/>
            </button>
        )

        const monthPanel = (
            <div className="flex h-full flex-col p-3" data-testid="calendar-month-picker-panel">
                <div className="mb-3 flex items-center justify-between">
                    <button type="button" onClick={() => setPickerMonth(addMonths(pickerMonth, -12))} aria-label={labels.previousPeriod} className={PANEL_NAV_BUTTON}>
                        <ChevronIcon direction="left" className="h-4 w-4"/>
                    </button>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{pickerMonth.getFullYear()}</span>
                    <button type="button" onClick={() => setPickerMonth(addMonths(pickerMonth, 12))} aria-label={labels.nextPeriod} className={PANEL_NAV_BUTTON}>
                        <ChevronIcon direction="right" className="h-4 w-4"/>
                    </button>
                </div>
                <div className="grid flex-1 grid-cols-3 grid-rows-4 items-center gap-x-2">
                    {Array.from({length: 12}, (_, monthIndex) => {
                        const monthDate = new Date(pickerMonth.getFullYear(), monthIndex, 1)
                        const selected = anchor.getFullYear() === monthDate.getFullYear() && anchor.getMonth() === monthIndex
                        return (
                            <button
                                key={monthIndex}
                                type="button"
                                aria-pressed={selected}
                                onClick={() => selectDate(monthDate)}
                                className={cn(
                                    'h-10 rounded-lg px-2 text-sm font-medium transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500/40',
                                    selected ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800'
                                )}
                            >
                                {dateTime.formatLocalDateMonthTitle(toDateKey(monthDate), {includeYear: false})}
                            </button>
                        )
                    })}
                </div>
            </div>
        )

        const dayPanel = (
            <div className="p-3" data-testid="calendar-day-picker-panel">
                <div className="mb-3 flex items-center justify-between">
                    <button type="button" onClick={() => setPickerMonth(addMonths(pickerMonth, -1))} aria-label={labels.previousMonth} disabled={Boolean(minMonth && pickerMonth <= minMonth)} className={PANEL_NAV_BUTTON}>
                        <ChevronIcon direction="left" className="h-4 w-4"/>
                    </button>
                    <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{dateTime.formatLocalDateMonthTitle(toDateKey(pickerMonth))}</span>
                    <button type="button" onClick={() => setPickerMonth(addMonths(pickerMonth, 1))} aria-label={labels.nextMonth} disabled={Boolean(maxMonth && pickerMonth >= maxMonth)} className={PANEL_NAV_BUTTON}>
                        <ChevronIcon direction="right" className="h-4 w-4"/>
                    </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-[11px] font-semibold leading-4 text-gray-500 dark:text-gray-400">
                    {pickerWeekdays.map((label, index) => <span key={`${label}-${index}`} className="text-center">{label}</span>)}
                </div>
                <div className="mt-1 space-y-1">
                    {Array.from({length: 6}, (_, rowIndex) => {
                        const weekDays = pickerDays.slice(rowIndex * 7, rowIndex * 7 + 7)
                        const weekStart = weekDays[0]
                        const disabledWeek = isWeekDisabled(weekStart)
                        const selectedWeek = view === 'week' && isSameDay(weekStart, startOfIsoWeek(anchor))
                        return (
                            <div
                                key={toDateKey(weekStart)}
                                data-testid={view === 'week' ? 'calendar-week-picker-row' : undefined}
                                className={cn(
                                    'grid grid-cols-7 gap-1 rounded-lg transition-colors',
                                    view === 'week' && !disabledWeek && 'hover:bg-primary-50 dark:hover:bg-primary-950/30',
                                    selectedWeek && 'bg-primary-100 dark:bg-primary-900/40'
                                )}
                            >
                                {weekDays.map(day => {
                                    const selected = view === 'week' ? selectedWeek : isSameDay(day, anchor)
                                    const outsideMonth = day.getMonth() !== pickerMonth.getMonth()
                                    return (
                                        <button
                                            key={toDateKey(day)}
                                            type="button"
                                            aria-pressed={selected}
                                            aria-label={dateTime.formatLocalDateWithWeekday(toDateKey(day), {
                                                dateStyle: 'long',
                                                weekday: 'long',
                                                context: 'sentence',
                                                includeYear: true
                                            })}
                                            disabled={disabledWeek}
                                            onClick={() => selectDate(view === 'week' ? weekStart : day)}
                                            className={cn(
                                                'flex h-9 w-9 items-center justify-center rounded-lg text-xs font-semibold transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500/40',
                                                disabledWeek
                                                    ? 'cursor-not-allowed text-gray-300 dark:text-gray-600'
                                                    : view === 'week' && selected
                                                        ? 'text-primary-800 dark:text-primary-200'
                                                        : selected
                                                            ? 'bg-primary-600 text-white'
                                                            : outsideMonth
                                                                ? 'text-gray-400 hover:bg-gray-50 dark:text-gray-600 dark:hover:bg-gray-800'
                                                                : 'text-gray-800 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-800',
                                                today !== null && isSameDay(day, today) && 'ring-1 ring-inset ring-primary-500'
                                            )}
                                        >
                                            {day.getDate()}
                                        </button>
                                    )
                                })}
                            </div>
                        )
                    })}
                </div>
            </div>
        )

        return (
            <div className={cn('flex min-w-0 items-center gap-2', className)} data-testid={testId}>
                <div className={cn('flex min-w-0 flex-1 items-center overflow-hidden rounded-xl border bg-white dark:bg-gray-900 shadow-sm', styles.frame, frameClassName)}>
                    {renderStep(previous, 'left', labels.previousPeriod)}
                    <div className={cn('flex h-full min-w-0 flex-1 items-center justify-center border-x', styles.divider)}>
                        <Overlay
                            open={open}
                            onOpenChange={setOpen}
                            title={labels.selectPeriod}
                            closeText={labels.close}
                            placement="bottom"
                            matchTriggerWidth={false}
                            className="max-h-[calc(100dvh-16px)] w-[360px] max-w-[calc(100vw-16px)] overflow-y-auto overscroll-contain !p-0"
                            trigger={(
                                <button
                                    type="button"
                                    data-testid="calendar-period-picker"
                                    aria-label={`${labels.selectPeriod}: ${viewLabel}, ${accessibleTitle}`}
                                    aria-expanded={open}
                                    className={cn('flex h-full w-full min-w-0 items-center justify-center gap-2 px-3 text-center transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset', styles.trigger)}
                                >
                                    <span className={cn('flex min-w-0 items-center gap-1 truncate text-sm font-semibold', styles.title)}>
                                        <span className={cn('shrink-0', styles.viewLabel, hideViewLabelOnCompact && 'hidden sm:inline')}>{viewLabel}</span>
                                        <span className={cn('shrink-0', styles.separator, hideViewLabelOnCompact && 'hidden sm:inline')} aria-hidden="true">·</span>
                                        <span className="min-w-0 truncate">{title}</span>
                                    </span>
                                    <ChevronIcon direction="down" className={cn('h-4 w-4 shrink-0 text-gray-500 dark:text-gray-400 transition-transform duration-normal ease-out motion-reduce:transition-none', open && 'rotate-180')}/>
                                </button>
                            )}
                        >
                            {views.length > 1 ? (
                                <div
                                    className={cn('border-b border-gray-200 p-2 dark:border-gray-700', viewSwitcherCompactOnly && 'lg:hidden')}
                                    data-testid="calendar-picker-view-switcher"
                                >
                                    <ViewTabs
                                        fill
                                        ariaLabel={labels.viewSwitcher}
                                        activeKey={view}
                                        onChange={key => onViewChange(key as CalendarPeriodView)}
                                        items={views.map(option => ({key: option, label: labels.views[option] ?? option}))}
                                        scrollLeftLabel={labels.previousPeriod}
                                        scrollRightLabel={labels.nextPeriod}
                                    />
                                </div>
                            ) : null}
                            {/* Both panels share one grid cell, so switching views keeps the taller panel's height. */}
                            <div className="grid">
                                {([['day', dayPanel], ['month', monthPanel]] as const).map(([panel, content]) => {
                                    const active = (view === 'month') === (panel === 'month')
                                    return (
                                        <div
                                            key={panel}
                                            aria-hidden={!active}
                                            inert={!active}
                                            className={cn(
                                                '[grid-area:1/1] transition-[opacity,scale,visibility,filter] duration-normal ease-out',
                                                // Reduced motion keeps the crossfade; visibility stays in the list so the outgoing panel can fade.
                                                'motion-reduce:transition-[opacity,visibility] motion-reduce:scale-100 motion-reduce:blur-none',
                                                // Scale, not translate: a shifted hidden panel would overflow and make the sheet scroll.
                                                active ? 'visible scale-100 opacity-100' : 'invisible scale-[0.98] opacity-0 blur-[2px]'
                                            )}
                                        >
                                            {content}
                                        </div>
                                    )
                                })}
                            </div>
                        </Overlay>
                    </div>
                    {renderStep(next, 'right', labels.nextPeriod)}
                </div>
                {onToday ? (
                    <button
                        type="button"
                        onClick={onToday}
                        className={cn('shrink-0 rounded-xl border bg-white dark:bg-gray-900 px-3 text-sm shadow-sm transition-colors focus:outline-hidden focus-visible:ring-2', styles.today)}
                    >
                        {labels.today}
                    </button>
                ) : null}
            </div>
        )
    }
}
